<script setup lang="ts">
import { getCurrentInstance, nextTick, onMounted, ref } from 'vue';
import { createObject, deleteObject, listObjects, updateObject } from '@/base/callBackend';
import { CabOrganization, CabTheme, uuidOf } from 'common';
// noinspection ES6UnusedImports - usages in <template> are not properly detected!
import { ORGANIZATION_CHANGE_EMIT, ORGANIZATION_REMOVE_EMIT } from '@/base/cabConstants';
import InstellingStrip, {ORGANIZATION_COLUMNS} from '@/views/admin/InstellingStrip.vue';
import PageHeader from '@/views/ui/PageHeader.vue';
import CardPanel from '@/views/ui/CardPanel.vue';
import RowGrid from '@/views/ui/RowGrid.vue';
import IconButton from '@/views/ui/IconButton.vue';
import {Plus} from 'lucide-vue-next';
import GeneralFilter from '@/views/general/GeneralFilter.vue';

const organizations         = ref<CabOrganization[]>([]);
const themes                = ref<CabTheme[]>([]);
const filterActive          = ref<boolean>(false);
const filteredOrganizations = ref<CabOrganization[]>([]);

onMounted(async () => {
    organizations.value = await listObjects(CabOrganization);
    themes.value        = await listObjects(CabTheme);
});

//=====================================================================================
async function onOrganizationRemove(organization: CabOrganization) {
    // the server refuses an instelling that still has report items; the row then stays
    if (await deleteObject(organization)) {
        organizations.value = organizations.value.filter((o) => uuidOf(o) !== uuidOf(organization));
    }
}

async function onOrganizationAdd() {
    filterActive.value    = false;
    const newOrganization = new CabOrganization();
    await createObject(newOrganization);
    organizations.value = [newOrganization, ...organizations.value];
    await nextTick();
    getOrganizationStrip(makeStripRef(newOrganization))?.firstEdit();
}

async function onOrganizationChange(organization: CabOrganization) {
    const uuid = uuidOf(organization);
    await updateObject(organization);
    organizations.value = organizations.value.map((o) =>
        uuidOf(o) === uuid ? organization : o,
    );
}

const instance = getCurrentInstance();

function getOrganizationStrip(ref: string): InstanceType<typeof InstellingStrip> | undefined {
    return (instance?.proxy?.$refs[ref] as any)?.[0];
}

function makeStripRef(x: CabOrganization) {
    return `OrganizationStrip-${x.uuid}`;
}

//=====================================================================================
</script>

<template>
    <div class="flex flex-col">
        <PageHeader title="Instellingen" :context="`${filteredOrganizations.length} van ${organizations.length}`">
            <template #before>
                <IconButton :icon="Plus" label="nieuwe instelling" variant="primary" data-testid="plus-button" @click="onOrganizationAdd()"/>
            </template>
            <template #actions>
                <GeneralFilter v-model:active="filterActive" v-model:cabList="organizations" v-model:cabListFiltered="filteredOrganizations" :id="'OrganizationList'"/>
            </template>
        </PageHeader>
        <CardPanel min-width="46rem">
            <RowGrid v-if="filteredOrganizations.length" :columns="ORGANIZATION_COLUMNS" header>
                <span class="pl-2">naam</span>
                <span class="pl-[11px]">thema's</span>
                <span class="pl-[11px]">deeltijd</span>
                <span>API</span>
                <span/>
            </RowGrid>
            <div v-else class="px-4 py-6 text-sm text-zzc-muted">geen instellingen</div>
            <InstellingStrip
                v-for="organization in filteredOrganizations"
                :key="uuidOf(organization)"
                :organization="organization"
                :allThemes="themes"
                :ref="makeStripRef(organization)"
                v-on:[ORGANIZATION_REMOVE_EMIT]="onOrganizationRemove"
                v-on:[ORGANIZATION_CHANGE_EMIT]="onOrganizationChange"
            />
        </CardPanel>
    </div>
</template>
